import React, { type FunctionComponent, useMemo } from 'react'
import { type DashTablePageSize } from 'legacy/dash-blocks/dash-table/utils/dashTable.types'
import { Table } from 'legacy/dash-components/table/Table'
import { makeOrdersListTableColumns } from 'legacy/sections/orders/components/list/components/table/utils/ordersListTable.utils'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import { type OrdersListBehaviour } from 'legacy/sections/orders/components/list/utils/ordersList.hooks'
import { useRPPTour } from 'utils/tour/tour.hooks'
import { type OrdersListApiOrder } from 'legacy/sections/orders/components/list/utils/ordersList.types'
import { usePickupOrderTour } from 'domains/pickup-settings/training-tour/pickupOrderTourSteps'
import { usePickupTourStatus } from 'domains/pickup-settings/training-tour/usePickupTourStatus'
import { RestartTourFooter } from 'domains/pickup-settings/training-tour/RestartTourFooter'
import { EmptyState, EmptyStateArtwork, EmptyStateTitle } from '@instacart/ids-core'
import orderEmptyStateImg from 'legacy/sections/orders/assets/orders-empty-state-illustration.png'
import { useIntl } from 'react-intl'
interface Props
  extends Pick<
    OrdersListBehaviour,
    | 'fetchData'
    | 'filters'
    | 'filteredOrders'
    | 'filteredOrderCount'
    | 'loading'
    | 'error'
    | 'orderType'
  > {}

// Not techincally valid in our dropdown menu, but we are
// using hidePageSizeSelector, so this configuration is ok
const pageSize = 20 as DashTablePageSize
const dummyOrder: OrdersListApiOrder[] = [
  {
    id: 123456,
    order_id: 123456,
    currency: 'CAD',
    initial_items_total: 'initial_items_total',
    items_total: '0.00',
    eta: '12pm-2pm',
    delivery_option: '12pm-2pm',
    shipping_price: '0.00',
    zone_id: 123213,
    status: 'In Progress - 0%',
    user_name: 'Sample order',
    store_name: 'Sample store',
    created_at: new Date().toDateString(),
  },
]

export const OrdersListTable: FunctionComponent<Props> = ({
  fetchData,
  filters,
  orderType,
  loading,
  error,
  filteredOrders,
  filteredOrderCount,
}) => {
  const { isOpen } = useRPPTour()
  const { redoTour } = usePickupOrderTour()
  const { isTourPending, isTourCompleted } = usePickupTourStatus('pickup_orders')
  const intl = useIntl()

  const hasAccess = useAccessControl()
  const showDummyOrder = filteredOrders.length === 0 && isOpen

  const showDeliveryOptionColumn = hasAccess({
    warehouseFlippers: Flipper.OrderColumnDeliveryOption,
  })

  const columns = useMemo(
    () => makeOrdersListTableColumns({ orderType, showDeliveryOptionColumn }),
    [orderType, showDeliveryOptionColumn]
  )
  const showPickupTourFooter = orderType === 'pickup' && (isTourPending || isTourCompleted)

  return (
    <Table
      initialPageSize={pageSize}
      manualPagination
      hidePageSizeSelector
      columns={columns}
      loading={loading}
      error={error}
      data={showDummyOrder ? dummyOrder : filteredOrders}
      totalRecordCount={filteredOrderCount}
      allowUnknownRecordCount
      filters={filters}
      fetchData={fetchData}
      tableFooterPageDetails={
        showPickupTourFooter ? <RestartTourFooter onRedoTour={redoTour} /> : null
      }
      emptyState={
        <EmptyState>
          <EmptyStateArtwork src={orderEmptyStateImg} />
          <EmptyStateTitle>
            {intl.formatMessage({
              id: 'orders.table.empty_state',
            })}
          </EmptyStateTitle>
        </EmptyState>
      }
    />
  )
}
